/**
 * SFCC-10874 — Catch-all PDP / category / Builder dispatcher.
 *
 * Replaces the previous trio of routes:
 *   - `/:1?/:2?/:3?/:4?/:5?/:6?/:productId[.html]`       (PDP, optional .html)
 *   - `/:1?/:2?/:3?/:4?/:5?/:6?/:productId.html`         (PDP, literal .html)
 *   - `*` → `routes/builderio/page.tsx`                  (Builder + category)
 *
 * Failure mode they exhibited: a category slug ending in `.html` (e.g.
 * `/weight-loss.html`) matched the PDP route, failed SCAPI getProduct, and
 * 404'd before reaching the catch-all that could have resolved it as a
 * category. This file consolidates the three into one decision tree.
 *
 * Order (load-bearing):
 *   1. Builder page lookup — authored pages take precedence over every
 *      SCAPI outcome (including redirects). Errors propagate (fail-closed).
 *      No Builder match falls through to step 2.
 *   2. BM redirect bundle lookup — mirrors SFRA RedirectURL precedence for
 *      URLRedirectMgr entries SCAPI does not surface. Fail-open.
 *   3. SCAPI `lookupUrlMapping(path)` — returns redirect + resourceType info
 *      in one cached call. Errors propagate (fail-closed).
 *   4. Redirect mapping fires immediately when `statusCode` +
 *      `destinationUrl` are set.
 *   5. resourceType dispatch (on TYPE, not URL-Rule-vs-direct-ID source):
 *        product       → render PDP via `runProductLoader`
 *        category      → render PLP via `runCategoryLoader`
 *        content[_asset] → proxy to SFRA (ProxyToSFRASignal)
 *        none / other  → proxy to SFRA (catch-all fallback)
 *
 * SFRA owns a handful of URLs that BM resolves to a `category`/`product`
 * URL Rule but renders as something else (e.g. `/hsa-eligibility-list`).
 * Those are force-proxied at the worker layer via the `LEGACY_LIST` env var
 * (`shouldProxyToSFRA`), before this loader ever runs — so the type dispatch
 * here can trust the SCAPI resourceType.
 *
 * Builder running first means an authored marketing page can hijack a URL
 * SCAPI would otherwise redirect or resolve to a PDP / PLP. That is the
 * intended override semantic — authors choose to take over a URL when they
 * publish a `page` model entry for it.
 *
 * The loader returns a discriminated union `{ type, data }` (with `seo`
 * hoisted alongside for PLP/PDP so DeferSeo can read it off the route
 * match). The default export switches on `data.type` to render the right
 * page-content component.
 *
 * SCAPI mapping `resourceType` casing varies by source — URL Rules return
 * lowercase (`"category"`, `"product"`, `"content"`); redirects + SDK
 * constants return uppercase (`"CATEGORY"`, `"PRODUCT"`, `"CONTENT_ASSET"`).
 * Compared case-insensitively below.
 */
import {
  type HeadersFunction,
  data,
  redirect,
  useLoaderData,
} from "react-router";

import {
  getSessionServerContext,
  isAuthenticatedUser,
} from "~/.server/sessions.server";
import { getTimingContext } from "~/.server/timing.server";
import { fetchBuilderPageTargetingInputs } from "~/hec/.server/builder-page-candidates.server";
import { shouldSkipPageLookup } from "~/hec/.server/builder-page-index.server";
import type { CIOBrowseProduct } from "~/hec/.server/cio-products.server";
import { pickVariantInputs } from "~/hec/ppr/ppr-variant.server";
import {
  buildAbTastyBuilderAttributes,
  stableTargetingDepKey,
} from "~/lib/abtasty.server";
import { mapTraitsToBuilderAttributes } from "~/lib/segment-profile.server";
import { loadSpenderPage } from "~/routes/fsastore-spender/spender-page.server";
import { runCategoryLoader } from "~/routes/product-list/loader/category-loader.server";
import { resolveBundledRedirect } from "~/seo/servers/redirect-bundle.server";
import {
  classifyRoute,
  loadRouteTruth,
  routeTruthConsultEnabled,
} from "~/seo/servers/route-truth.server";
import { lookupUrlMapping } from "~/seo/servers/url-resolution.server";

import { getBuilderSearchParams, isPreviewing } from "@builder.io/sdk-react";

import { ProxyToSFRASignal } from "~/.server/proxy-to-sfra-signal";
import { getRequestDeadline } from "~/.server/request-deadline";
import { SlasMintThrottledError } from "~/commerce-api/slas-mint-throttle";
import { PDPErrorBoundary } from "~/components/error-boundries/pdp-error-boundary";
import {
  builderCacheControl,
  swrFetchOneEntry,
} from "~/hec/.server/builder-cache";
import { buildPageCacheTags } from "~/hec/.server/builder-page-cache-tags";
import {
  fetchReferencesForEditor,
  resolveAndInlineCtaReferences,
} from "~/hec/.server/builder-reference-cache";
import { getBuilderConfig } from "~/hec/.server/config";
import { resolvePageType } from "~/hec/.server/page-type";
import { findRenderedBlockOptions } from "~/hec/builder/block-names";
import { ModelMap } from "~/hec/builder/model-map";
import { loadCustomComponents } from "~/hec/builder/registry-loader";
import { SpenderProvider } from "~/hec/components/spender-page/spender-provider";
import {
  isEnabled,
  parseIntegrationMode,
  resolveBuilderPageIndexMode,
} from "~/hec/config/integration-flags";
import { isHraHost } from "~/hec/hra/host";
import { markPprShellBuildDegraded } from "~/hec/ppr/ppr-context";
import { extractProductCategoryConfig } from "~/hec/utils/builder-carousel-config";
import { transformBuilderPageEntry } from "~/hec/utils/builder-image-priority";
import { stabilizeBuilderPixelIds } from "~/hec/utils/builder-pixel";
import { collectAllUnresolvedReferences } from "~/hec/utils/builder-reference-collector";
import { isSfraFlagged } from "~/hec/utils/builder-routing-flags";
import { slimBuilderPageForFlight } from "~/hec/utils/builder-trim-references";
import { stashSsrBuilderPage } from "~/hec/utils/ssr-builder-page";
import {
  hasEmbeddedChromeBlock,
  stripEmbeddedChromeBlocks,
} from "~/hec/utils/strip-embedded-chrome";
import { isTransientUpstreamError } from "~/hec/utils/transient-error";
import { isBuilderEditorOrPreviewRequest } from "~/lib/builder-editor-request";
import type { LoaderArgs } from "~/lib/loadargs.types";
import {
  SPENDER_BLOCK_NAME,
  SPENDER_CHIP_ROW_BLOCK_NAME,
  useSpenderPageMount,
} from "~/routes/fsastore-spender/spender-page";
import { runProductLoader } from "~/routes/product-details/loader/product-detail-loader";
import { ProductPageContent } from "~/routes/product-details/product-details";
import { headers as plpHeaders } from "~/routes/product-list/components/headers";
import { CategoryPageContent } from "~/routes/product-list/shop.$categoryId";
// The dispatch ORDER is defined once, here, and shared with /dev/url-trace
// (SFCC-12092). Do not re-implement the priority ladder in either caller.
import { evaluateDispatch } from "~/seo/servers/url-trace-dispatch";
import {
  type BuilderPageRobotsSeo,
  builderPageRobotsSeo,
} from "~/seo/utils/builder-page-robots";
import { mergeTrackingParams } from "~/seo/utils/tracking-params";
import { categoryRefRedirectTarget } from "~/shop/utils/categoryref";

import type { Route } from "./+types/$";
import {
  type BuilderPagePayload,
  BuilderPageView,
  builderEntryMeta,
} from "./builder-page-view";

export { PDPErrorBoundary as ErrorBoundary };

// Category PLPs delegate Cache-Control to product-list/components/headers.tsx
// so the SFCC-10837 LCP comparison (PR #148 `private` vs PR #162 `Vary: Cookie`)
// can swap strategies in one place. Other branches (PDP, Builder, redirects)
// keep their hardcoded headers and pass through verbatim. The discriminator
// `x-page-type: plp` is set by the category branch below.
export const headers: HeadersFunction = args => {
  const passthrough = Object.fromEntries(args.loaderHeaders.entries());
  if (args.loaderHeaders.get("x-page-type") === "plp") {
    return { ...passthrough, ...plpHeaders(args) };
  }
  return passthrough;
};

// Lives in its own module so the unit tests can import it without dragging
// in the dispatcher's server-only deps. See dispatcher-should-revalidate.ts
// for the full rationale.
export { dispatcherShouldRevalidate as shouldRevalidate } from "./dispatcher-should-revalidate";

// BuilderPagePayload moved to ./builder-page-view.tsx (COM-188) so the
// homepage route can import the Builder view without this dispatcher's
// PDP/PLP component graph. Re-imported above; shape unchanged.

type BuilderLookupResult = {
  payload: BuilderPagePayload;
  headers: Record<string, string>;
  pageType: string;
  /** COM-474: set when the page carries the spender marker block. */
  spender?:
    | (Awaited<ReturnType<typeof loadSpenderPage>> & { persist: boolean })
    | null;
  /**
   * COM-841: robots override when the entry's `noIndex` switch is on. Read
   * from the FULL page here — `payload.page` is the slim flight copy, which
   * drops `noIndex`.
   */
  seo?: BuilderPageRobotsSeo;
} | null;

/**
 * Minimal Builder "preview shell" payload. Returned ONLY in preview mode when
 * the server fetch yields no renderable `page` entry yet — a brand-new or
 * never-persisted draft whose content lives only in the editor. It carries a
 * `null` page so `BuilderPageView` still mounts; that lets
 * `useBuilderPreviewContent`'s `subscribeToEditor` overlay the editor's live
 * content instead of the dispatcher falling through to SFRA's 404 page. This is
 * the canonical Builder SSR pattern: render content `|| isPreviewing()`.
 */
async function buildBuilderPreviewShell(
  path: string,
  buildVersion: string,
): Promise<BuilderLookupResult> {
  const pageType = await resolvePageType(path === "/" ? "home" : "content");
  return {
    payload: {
      path,
      page: null,
      references: {},
      unresolvedCount: 0,
      hideLayoutHeader: false,
      hideLayoutFooter: false,
      cioProducts: [],
      carouselCategoryId: null,
      // Triage C8 (PR #1121 review round 1): every BuilderPagePayload
      // constructor carries the stamp — an open editor on a new/unpersisted
      // draft is a long-lived tab too, and stays skew-protected across deploys.
      buildVersion,
    },
    // Preview is never cached (`builderCacheControl(true)` → `no-store`), so the
    // auth-aware `isRegistered` arg is irrelevant here.
    headers: {
      "Cache-Control": builderCacheControl(true),
      "X-Cache-Tags": "builder-page-preview",
    },
    pageType,
  };
}

/** COM-853 kill switch, fail-closed: an unreadable env means "off". */
function builderPageIndexEnabled(): boolean {
  try {
    return isEnabled(
      resolveBuilderPageIndexMode(getSessionServerContext().env ?? {}).mode,
    );
  } catch {
    return false;
  }
}

/**
 * Fetch a Builder `page` entry for the given path and decide what to do
 * with it. Returns the cleaned-up payload + per-request headers when a
 * page exists with renderable content, or `null` when:
 *
 *   - Builder has no page for this URL
 *   - The entry exists but has `data.isSfraPage: true` (the Builder
 *     entry is a CMS handle for an SFRA-owned URL; per the SFCC-10874
 *     review, the dispatcher ignores such entries and lets the normal
 *     SCAPI / 404 matrix run. SFRA-owned URLs that should be proxied
 *     are handled by `legacy-routes.ts` at the worker layer)
 *   - The entry exists but has no renderable blocks (draft / chrome-only)
 *   - COM-474: the entry is the spender page and its app cannot load
 *     (WORKERS_FSA_SPENDER_MODE not "on", gate off, or load failed).
 *     `/dev/url-trace` does not model this one.
 *   - The page-tree lookup failed with a TRANSIENT upstream error (timeout,
 *     dropped connection — SFCC-11887). Same `null` as a clean miss, so the
 *     cascade below it is unchanged (COM-852).
 *
 * Every other error propagates. The dispatcher fails closed on non-transient
 * Builder errors rather than silently falling through to SCAPI — a genuine
 * Builder fault shouldn't silently change what page a URL resolves to.
 */
async function tryBuilderPage({
  request,
  context,
  path,
  searchParams,
}: {
  request: Request;
  context: LoaderArgs["context"];
  path: string;
  searchParams: URLSearchParams;
}): Promise<BuilderLookupResult> {
  const apiKey = getBuilderConfig(context.preset).api_key;
  const previewing = isPreviewing(searchParams);
  // The wider "will receive live editor contentUpdate payloads" flag, derived
  // ONCE here (COM-586). `previewing` stays the SDK's `builder.preview=`
  // notion that gates includeUnpublished / the preview shell / CTA inlining.
  const editorOrPreview = isBuilderEditorOrPreviewRequest(new URL(request.url));
  const builderSearchParams = getBuilderSearchParams(searchParams);

  // COM-853: skip the lookup when the site's cached `page` listing proves no
  // entry targets this URL — nearly every PDP. Only from an index this isolate
  // already holds (a miss loads it in the background and looks up as before),
  // and never for preview / editor traffic, Builder query overrides, or a PPR
  // shell build (which must record its variant inputs from the candidates).
  // Neither helper throws: any failure in this optimisation means "look up".
  if (
    !previewing &&
    !editorOrPreview &&
    !context.pprShellBuild &&
    Object.keys(builderSearchParams).length === 0 &&
    builderPageIndexEnabled() &&
    shouldSkipPageLookup(apiKey, path, context.waitUntil)
  ) {
    return null;
  }

  const [traits, abtastyEvaluation] = await Promise.all([
    context.traits,
    context.abtastyEvaluation,
  ]);
  const { time: timeSegment } = getTimingContext();

  // Edge-cache the non-personalized page-tree lookup (not the rendered
  // document — that stays `private` for personalized chrome). Tagged to match
  // the document so a future purge-by-tag busts both; off in preview.
  //
  // Fail-soft ONLY on a *transient* upstream error: a timeout (NOT retried
  // since COM-837 — `swrFetchOneEntry` passes `retryTimeouts: false`) or a
  // dropped connection that survived its one retry. We treat it as "no Builder
  // page" and NOTHING more: the dispatcher runs the rest of the cascade exactly
  // as it does for a clean Builder miss — bundled redirect, SCAPI URL-rule
  // redirect, SCAPI mapping (product → Workers PDP, category → Workers PLP,
  // content → SFRA), and the SFRA `catch-all-fallback` only when nothing maps
  // the URL. A Builder failure never decides that a URL belongs to SFRA
  // (COM-852 — the measured load-test trace and counts are on the ticket).
  // Before SFCC-11887 this propagated and
  // painted the "services offline" boundary — a Builder brown-out took the
  // whole page down. A NON-transient error still propagates (fail-closed): a
  // genuine bug or bad response must surface, not silently change what a URL
  // resolves to.
  let abtastyVariations = buildAbTastyBuilderAttributes(abtastyEvaluation);
  let builderTraitAttrs = mapTraitsToBuilderAttributes(traits);
  // COM-482 shell build (variant keying, docs/ppr-shell.md): this render
  // becomes every guest's shell for ONE variant, so the page lookup must be
  // a function of the variant alone — not of the synthetic request (whose
  // evaluation is suppressed) and not of the visitor whose miss kicked the
  // build. The variant inputs are the targeting keys of every entry Builder
  // could serve for this path (a targeting-blind candidate listing, SWR-
  // cached under the path's tags); the lookup is pinned to the visitor's
  // values for exactly those keys, and what was pinned is recorded so the
  // Worker keys the shell by it. Two visitors with the same values for the
  // inputs get one shell; nothing else about them can reach the tree.
  // Errors propagate: without the inputs the shell cannot be keyed, and the
  // build refuses to cache rather than guess.
  const shellBuild = context.pprShellBuild;
  if (shellBuild) {
    const candidates = await timeSegment(
      () => fetchBuilderPageTargetingInputs(apiKey, path),
      {
        type: "builder-page-candidates",
        desc: "Builder page candidate listing (shell variant inputs)",
      },
    );
    const pinned = pickVariantInputs(candidates.inputs, shellBuild.attributes);
    shellBuild.variant = {
      inputs: candidates.inputs,
      attributes: pinned,
      candidateIds: candidates.candidateIds,
      ...(candidates.truncated ? { truncated: true } : {}),
    };
    // The pinned pairs replace BOTH request-derived groups: trait keys and
    // flag keys alike are only ever seen through the pin here.
    abtastyVariations = {};
    builderTraitAttrs = pinned;
  }
  // Wave 2A TTFB attribution: the page-tree lookup and the CTA-reference
  // inlining below are the two segments of this loader that can spike on an
  // SWR miss/revalidate; timed separately so the `catch-all-loader-total`
  // envelope is attributable.
  let page: Awaited<ReturnType<typeof swrFetchOneEntry>>;
  try {
    page = await timeSegment(
      () =>
        swrFetchOneEntry(
          {
            model: "page",
            apiKey,
            // In preview, surface draft + scheduled (future `startDate`) entries so
            // authors can preview unpublished/scheduled pages. Strictly gated on
            // `previewing` (the URL carries `builder.preview=`), so production stays
            // published-only and a real shopper never sees draft content. This is what
            // makes a standalone "Preview" tab — which has no live `subscribeToEditor`
            // postMessage — actually receive the draft from the server.
            ...(previewing ? { includeUnpublished: true } : {}),
            options: {
              url: path,
              // Do NOT set `enrich`. The Builder SDK already forces
              // `includeRefs=true`, which inlines referenced entries as
              // `reference.value.data` (e.g. each carousel/CTA item's `link` page
              // target). `enrich` runs a second resolution pass that STRIPS that
              // inlined `value` from references nested inside custom-component list
              // fields, leaving their hrefs unresolved (carousel items → "#").
              // (`enrichOptions.enrichLevel` is ignored by the SDK.) See SFCC-11308.
              ...builderSearchParams,
            },
            userAttributes: {
              urlPath: path,
              ...builderTraitAttrs,
              ...abtastyVariations,
            },
          },
          {
            name: "builder-page",
            // SFCC-12121: `new_user` deliberately is NOT a separate dep here —
            // it never reaches `userAttributes` directly (see the module doc for
            // why: raw AB Tasty context inputs flow only into campaign
            // evaluation, never straight into Builder). Once a live campaign
            // targets it, the campaign's differing decision already lands in
            // `abtastyVariations` below, which IS deps'd — adding the raw signal
            // too would only fragment the cache with no isolation benefit, and
            // would cold-start this cache for the entire anonymous population on
            // every Segment outage (a round-2 review finding on this ticket).
            deps: [
              path,
              builderSearchParams,
              stableTargetingDepKey(abtastyVariations),
              stableTargetingDepKey(builderTraitAttrs),
            ],
            enabled: !previewing,
            // id + path + inlined-reference tags (all base-id normalized), matching
            // exactly what the invalidation webhook purges. See `buildPageCacheTags`.
            tags: entry =>
              buildPageCacheTags(entry, path, { warnOnOverflow: true }),
            // Slim the cached tree before it serializes into HTML / flight data
            // (page-ref `value.data` reduced to url/title/name, editor metadata
            // dropped — see ~/hec/utils/builder-trim-references), then flag the
            // first above-the-fold hero image group `highPriority` so it SSRs
            // eager + fetchpriority=high and is LCP-eligible (COM-181 — see
            // ~/hec/utils/builder-image-priority).
            transform: transformBuilderPageEntry,
          },
        ),
      { type: "builder-page-tree", desc: "Builder page-tree SWR lookup" },
    );
  } catch (err) {
    if (!isTransientUpstreamError(err)) throw err;
    // Keep the `[tryBuilderPage] transient Builder error` prefix stable — the
    // log-audit signature and the worklist ledger match on it. The tail says
    // where the request goes next; it is NOT necessarily SFRA (COM-852).
    console.warn(
      `[tryBuilderPage] transient Builder error (timeouts are not retried) — no Builder page; ` +
        `dispatch continues (redirects → SCAPI mapping → PDP/PLP; SFRA only if content or unmapped) for ${path}`,
      err instanceof Error ? err.message : err,
    );
    page = null;
  }

  if (!page) {
    // Preview: a brand-new / never-persisted page has no server entry yet — its
    // content arrives client-side via `subscribeToEditor`. Render an empty
    // Builder shell so that overlay can mount instead of falling through to
    // SFRA's 404 (which is what authors saw when creating a new page).
    if (previewing) return buildBuilderPreviewShell(path, context.buildVersion);
    return null;
  }

  const pageData = page.data as Record<string, unknown> | undefined;

  // Authors flag a Builder entry as "SFRA-owned content" via
  // `data.isSfraPage: true`. The Builder entry is just a CMS handle for
  // SEO / URL targeting metadata; SFRA owns the render. The dispatcher
  // IGNORES these entries (treats them as no Builder match) and lets the
  // normal SCAPI / 404 matrix run. Actual SFRA proxying for these URLs is
  // handled at the worker layer via `legacy-routes.ts` patterns. Accept
  // boolean `true` and string `"true"` since Builder text-type fields
  // surface as strings.
  const isSfraFlag = isSfraFlagged(pageData);

  if (isSfraFlag) {
    console.info(
      "[catch-all] Builder entry flagged isSfraPage; ignoring and falling through to SCAPI",
      { path, builderPageId: page.id ?? "(none)" },
    );
    return null;
  }

  const useDefaultFooter = pageData?.useDefaultFooter !== false;
  // SFCC-11840: default true when the field is missing — a page only opts OUT of
  // the shared native header by setting `useDefaultHeader: false` explicitly.
  const useDefaultHeader = pageData?.useDefaultHeader !== false;
  // Probed BEFORE the strip below removes the block. `useDefaultHeader: false`
  // is ambiguous on its own; pairing it with this tells the two author intents
  // apart. See `hideLayoutHeader` below.
  const embedsOwnHeader = hasEmbeddedChromeBlock(page, "header");

  // `page` arrives already ref-trimmed from the cached fetch (transform above).
  // stripHeader stays unconditionally true: embedded Builder header components
  // aren't ported (Task B), so any embedded header Symbol is always removed and
  // the native `<HecHeader>` is the only header chrome we can actually render.
  const cleanedPage = stripEmbeddedChromeBlocks(page, {
    stripHeader: true,
    stripFooter: useDefaultFooter,
  }) as typeof page;

  // Builder can return a page "shell" (metadata + targeting hit, but no
  // published blocks) when an entry exists at this URL in a draft state —
  // visible in the editor preview, but with nothing to render in
  // production. Treat that as no match so the dispatcher falls through to
  // SCAPI instead of hijacking the URL with an empty page.
  //
  // Same check guards the case where every block was a chrome Symbol that
  // `stripEmbeddedChromeBlocks` removed — leaving zero renderable blocks.
  const cleanedBlocks = (cleanedPage?.data as Record<string, unknown>)?.blocks;
  if (!Array.isArray(cleanedBlocks) || cleanedBlocks.length === 0) {
    // Preview: an entry exists but has no published/renderable blocks (a draft
    // shell, or chrome-only after Symbol stripping). Keep going so the editor
    // overlay (`subscribeToEditor`) can render the in-progress content; in
    // production treat it as no match and fall through to SCAPI.
    if (!previewing) {
      console.warn(
        "[catch-all] Builder entry returned empty/draft blocks, falling through to SCAPI",
        { path, builderPageId: page.id ?? "(none)" },
      );
      return null;
    }
  }

  // COM-474: the spender marker loads the data its sibling spender blocks
  // render from, on whatever URL the page has (COM-883), started here so it
  // overlaps the lookups below. Gate off (non-FSA, WORKERS_FSA_SPENDER_MODE
  // not "on") or a failed load → SFRA, as isSfraPage does.
  const spenderOptions = findRenderedBlockOptions(
    cleanedPage,
    SPENDER_BLOCK_NAME,
  );
  const spenderOn =
    parseIntegrationMode(
      getSessionServerContext().env?.WORKERS_FSA_SPENDER_MODE,
    ) === "on";
  const spenderTask = spenderOptions
    ? (spenderOn
        ? loadSpenderPage({
            request,
            context,
            // ponytail: first SpenderChipRow block wins; a second one renders the same list.
            authoredChips: findRenderedBlockOptions(
              cleanedPage,
              SPENDER_CHIP_ROW_BLOCK_NAME,
            )?.chips,
          })
        : Promise.reject(
            new Response(null, { status: 404, statusText: "flag=off" }),
          )
      ).then(
        spenderPage => ({
          ...spenderPage,
          persist: spenderOptions.persistBalance === true,
        }),
        (err: unknown) => {
          const gated = err instanceof Response;
          const reason = gated
            ? `status=${err.status} ${err.statusText}`.trim()
            : err instanceof Error
              ? err.message
              : String(err);
          (gated ? console.info : console.warn)(
            `[catch-all] spender mount unavailable (${reason}), falling through to SCAPI path=${path}`,
          );
          return null;
        },
      )
    : null;

  // Collected BEFORE the CTA inlining below, so this map stays what it always
  // was: FULL content for every unresolved reference. Every `useReference`
  // consumer keeps working unchanged — `BuilderReferenceContent` renders
  // `value.data` as Builder content and needs `data.blocks`, which the CTA
  // projection deliberately strips. It is also what the client falls back to
  // after the post-idle `/resources/builder-page-tree` fetch, which does not
  // inline references.
  // In the Builder editor the map ALSO carries the page's direct references
  // (already-inlined ones seeded, not refetched) because live `contentUpdate`
  // payloads strip inlined values (COM-586); on shopper requests `entry` is
  // undefined and this is exactly the `fetchReferencesCached` call it was.
  const unresolvedReferences = collectAllUnresolvedReferences(cleanedPage);
  const resolvedReferences = fetchReferencesForEditor(
    editorOrPreview ? cleanedPage : undefined,
    apiKey,
    path,
    unresolvedReferences,
  );

  // COM-198: resolve the CTA references server-side and inline them as
  // `reference.value` BEFORE the tree is stashed for SSR, so the CTA blocks read
  // a url synchronously and open no Suspense boundary. Without this every CTA
  // suspended and React shipped the fallback AND the resolved copy — 466
  // duplicated `builder-id`s and a 751,736-char tail on FSA.
  //
  // Bounded to CTA option FIELDS (not merely to the `page` model), to four
  // projected fields per entry, and to an overall wall-clock budget. Anything it
  // cannot resolve keeps the streaming path via the map above.
  //
  // Skipped on production `.data` requests: the inlined tree is read ONLY by the
  // SSR stash below (which `context.isDataRequest` skips) and by the preview
  // payload, while a `.data` flight ships `slimBuilderPageForFlight`, which drops
  // `blocks` entirely — so resolving for one is pure waste. That waste is
  // amplified, not marginal: `PrefetchBuilderAnchors` fires `.data` prefetches on
  // hover AND on viewport intersection, so one page view can fan out several,
  // each paying up to ~46 Builder lookups and each able to consume the full
  // budget.
  if (previewing || !context.isDataRequest) {
    const ctaRefs = await timeSegment(
      () => resolveAndInlineCtaReferences(cleanedPage, apiKey),
      {
        type: "cta-ref-inline",
        desc: "CTA reference inlining",
      },
    );
    // COM-482: a shell built from a partly inlined tree would serve the
    // streaming fallback for those CTAs to every guest, and its page digest
    // would never match a warm request's fully inlined tree.
    if (ctaRefs.unresolved > 0) {
      markPprShellBuildDegraded(shellBuild, "cta-references");
    }
  }

  const carouselConfig = extractProductCategoryConfig(
    cleanedPage,
    "homepageCarouselGrid",
  );
  // Do not await CIO browse on the document TTFB path. The carousel already
  // client-fetches via `useFetchCioProductCategory` when SSR products are
  // empty (skeletons until the browse returns). Prefetching here serialized
  // every tile into the flight payload and blocked first byte. The spender
  // mount (above) does await its grid: see the trade-off in loadSpenderPage.
  const cioProducts: CIOBrowseProduct[] = [];
  const carouselCategoryId = carouselConfig?.categoryId ?? null;

  // Builder-rendered pages are "home" for `/`, otherwise "content" for the
  // GTM pageType mapping. Other Builder-handled URL surfaces (cart, learn,
  // favorites, …) are SFRA-served on Workers today — if any begin
  // Builder-resolving here, port the substring branches.
  const pageType = await resolvePageType(path === "/" ? "home" : "content");
  // Document Cache-Control is auth-aware (SFCC-11297): a registered shopper's
  // document SSRs personalized chrome, so it must never be browser-cached.
  const isRegistered = await isAuthenticatedUser();
  const spender = spenderTask && (await spenderTask);
  if (spenderTask && !spender) return null;

  // Preview keeps the full tree in the flight so subscribeToEditor can
  // overlay drafts. Production stashes the tree for SSR only and sends a
  // slim copy (id + SEO fields) in the first flight.
  //
  // DOCUMENT REQUESTS ONLY: the stash is cleared by entry.server's
  // handleRequest when the shell render settles, but `.data` requests
  // (client navigations, hover/viewport prefetches from
  // PrefetchBuilderAnchors) never reach handleRequest — a stash for them
  // would sit in the isolate until FIFO eviction. Their flight is the slim
  // page anyway; the client fetches /resources/builder-page-tree for blocks.
  const ssrKey = context.isDataRequest ? undefined : context.cspNonce;
  if (!previewing && ssrKey) {
    // Pixel ids once more on the final tree: references inlined from their own
    // cache may predate the fetch-time rewrite (see builder-pixel.ts, COM-482).
    stashSsrBuilderPage(ssrKey, stabilizeBuilderPixelIds(cleanedPage));
  }

  return {
    payload: {
      path,
      page: previewing
        ? (cleanedPage as BuilderPagePayload["page"])
        : slimBuilderPageForFlight(cleanedPage),
      // Always shipped (slim flight or not): this map only carries references
      // the collector found UNRESOLVED (no inlined `value`), and it is the
      // ONLY data source for `useReference` consumers (GoalTile,
      // SuspendedBuilderReferenceContent) — both during the SSR-island render
      // and after the post-idle tree fetch, which does not re-resolve refs.
      // Zeroing it in production silently blanked those blocks.
      references: resolvedReferences,
      unresolvedCount: unresolvedReferences.size,
      // SFCC-11840. Suppress the native header ONLY when the page opts out AND
      // brings no header of its own — "no header at all", which is the toggle's
      // documented expected behavior. When the page opts out but DOES embed a
      // header Symbol, that Symbol was just stripped above (its components are
      // unported), so suppressing the native header too would leave the page
      // with no header whatsoever — a regression on live content. Keep the
      // native header in that case until Task B ports the embedded header.
      hideLayoutHeader: !useDefaultHeader && !embedsOwnHeader,
      hideLayoutFooter: !useDefaultFooter,
      cioProducts,
      carouselCategoryId,
      ssrKey: previewing ? undefined : ssrKey,
      // Triage C8 (release-15 review): the serving build's RR7 asset-manifest
      // version (`build.assets.version`, same value keying the guest HTML
      // cache). A stale tab's soft nav fetches this payload as a `.data`
      // flight from a NEWER server whose slim shape its bundle has never
      // seen; `BuilderPageView` compares this stamp against the LOADED
      // bundle's `__reactRouterManifest.version` and heals with one guarded
      // document reload instead of misrendering. Forward-only by nature: a
      // client older than this field never checks it.
      buildVersion: context.buildVersion,
    },
    headers: {
      // COM-185: the third arg marks a STRICTLY-COOKIELESS request (no Cookie
      // header at all) — no auth, no price-test variant, no attribution — so
      // the anonymous document drops `private` and becomes storable by the
      // Worker guest HTML edge cache. Cookie-carrying guests keep
      // `private, max-age=60` (SFCC-11297).
      // `personalized`: the evaluation assigned ≥1 campaign, so this document
      // is a personalization variant — `private` always, WINNING over the
      // cookieless relaxation (gap-window fix, jam 46b8e6e1; see
      // builderCacheControl — the mint request is itself cookieless and can
      // carry an assigned arm). `?.` mirrors the
      // buildAbTastyBuilderAttributes tolerance contract: a deadline fallback
      // or minimal test context degrades to variant-free, never throws.
      "Cache-Control": builderCacheControl(
        previewing,
        isRegistered,
        !request.headers.get("cookie")?.trim(),
        { personalized: (abtastyEvaluation?.assigned.length ?? 0) > 0 },
      ),
      // Tags computed from `page` — the EXACT transformed tree the SWR page-tree
      // cache stored and tagged (the `tags` callback above got this same object),
      // so the document and the tree carry an identical tag set and a purge busts
      // both. NOT `cleanedPage`: chrome-Symbol stripping would drop ref tags the
      // SWR entry keeps (a ref living only inside a chrome block), and would also
      // let the `MAX_REF_TAGS` truncation pick a different subset — either way the
      // document and tree diverge. Chrome invalidation is handled separately via
      // the layout group tags stamped on every document (document-cache.middleware).
      // Comma-joined per the X-Cache-Tags contract (CacheMiddleware splits on comma).
      "X-Cache-Tags":
        buildPageCacheTags(page, path).join(",") || "builder-page-unknown",
    },
    pageType,
    spender,
    seo: builderPageRobotsSeo(cleanedPage, path),
  };
}

export async function loader(args: LoaderArgs) {
  const { createTimer } = getTimingContext();
  const [endLoaderTimer] = createTimer(
    "catch-all-loader-total",
    "Catch-all dispatcher total",
  );
  try {
    return await loaderImpl(args);
  } catch (error) {
    // SFCC-11918. The SFCC-11922 mint throttle deliberately refuses to call
    // SLAS for ~8s after an `oauth2/token` 429, and `Auth.ready()` throws
    // `SlasMintThrottledError` for the duration. That is correct back-pressure
    // — but the dispatcher's SCAPI calls are fail-CLOSED, so the throw reached
    // `HandleError` and every shopper served during a cooldown window got
    // "Looks like this page is broken." Confirmed in prod on
    // `/womens-health.html` at 14:37 / 14:39 / 14:42 / 14:43 UTC 2026-08-12.
    //
    // This is the LAST-RESORT net. The primary fix is in the fan-out below,
    // which degrades a throttled SCAPI mapping to `null` so a Builder page
    // still renders — proxying a Builder-authored URL to SFRA would 404, since
    // SFRA has no such page. This catch only handles a throttle thrown from
    // somewhere the cascade cannot absorb (e.g. inside a PLP/PDP loader after
    // dispatch), where SFRA — which carries its own session and needs no SLAS
    // token — beats an error page.
    //
    // Deliberately NOT a retry: re-minting immediately is precisely the storm
    // the throttle exists to stop, and the cooldown would still be active. For
    // the same reason this must never be added to `isTransientUpstreamError` —
    // see `tests/unit/hec/slas-throttle-not-retryable.test.ts`.
    if (error instanceof SlasMintThrottledError) {
      const path = new URL(args.request.url).pathname;
      console.warn(
        "[catch-all] SLAS mint throttled → proxying to SFRA instead of 500",
        { path },
      );
      throw new ProxyToSFRASignal("slas-mint-throttled", path);
    }
    throw error;
  } finally {
    endLoaderTimer();
  }
}

/**
 * Mutation counterpart to the loader's `catch-all-fallback` (SFCC-12077).
 *
 * The loader falls back to SFRA for any GET it can't resolve, which made
 * `LEGACY_ROUTE_PATTERN_SOURCES` a fast-path hint list rather than the only
 * safety net (SFCC-10874). That fallback lives *inside the loader*, and React
 * Router resolves `405 Method Not Allowed` at route-match time for a POST to a
 * route with no `action` — before any loader runs. So the backstop covered GETs
 * only, and every POST to an SFRA-owned path missing from the pattern list died
 * with a 405 whose body was our rendered HTML document.
 *
 * That is how magic-link initiation broke: SFRA's `passwordResetForm.isml`
 * posts to the lowercase URL-Rules alias `/generate-password-magiclink`
 * (`Account-GenerateMagicLink`), which matches no pattern — not
 * `^/on/demandware`, and not the `^/[A-Z][A-Za-z0-9]*-[A-Z]` controller
 * heuristic, because the alias is lowercase. `login.js` requests it with
 * `dataType: 'json'`, so the HTML 405 body failed to parse and the shopper got
 * "Something went wrong, please try again later."
 *
 * Reaching this action already *means* the path is unlisted: every RR7 route
 * (including every `/resources/*` and `/api/*` action) is matched ahead of the
 * splat, so RR7's own matcher computes the "not ours" set — no hand-maintained
 * mirror of the route table to drift out of lockstep.
 *
 * This throws before touching `request.body`, which is load-bearing:
 * `proxyToSFRA` forwards `body: request.body` as a stream, so consuming it here
 * (a `request.formData()` call, say) would surface as "ReadableStream is
 * disturbed" at the proxy hop. The worker's signal catch defends this invariant
 * explicitly — see the `bodyUsed` guard in `cloudflare/worker.server.ts`.
 */
export async function action({ request }: Route.ActionArgs) {
  const url = new URL(request.url);
  if (isHraHost(url.hostname)) throw hraHostNotFound();
  const path = url.pathname;
  // Logged (unlike the loader's steady-state fallback) because a mutation
  // reaching the catch-all means an SFRA-owned write path is missing from
  // LEGACY_ROUTE_PATTERN_SOURCES. Low-volume and worth promoting to a pattern.
  console.info("[catch-all] unmatched mutation → proxying to SFRA", {
    path,
    method: request.method,
  });
  throw new ProxyToSFRASignal("catch-all-action-fallback", path);
}

/**
 * ETH-106. The HRA host (`joinpatiently.*`) serves no store page. Its worker
 * guard (`cloudflare/hra-host-guard.ts`) admits only the HRA routes, the
 * intake, the resource routes they need, assets and the SFRA proxy, so a
 * request reaching this dispatcher there is a path nothing owns (an unknown
 * `/resources/*` name, a missing asset). A 404 before any Builder, redirect
 * bundle or SCAPI lookup: never an FSA-branded page, redirect or 404 on a
 * partner's walled garden.
 */
function hraHostNotFound(): Response {
  return new Response("Not Found", {
    status: 404,
    headers: {
      "Cache-Control": "private, no-store",
      "X-Robots-Tag": "noindex, nofollow",
    },
  });
}

async function loaderImpl({ request, context }: LoaderArgs) {
  const url = new URL(request.url);
  if (isHraHost(url.hostname)) throw hraHostNotFound();
  // NOTE (PR #1042 blocker 10, correcting the old COM-67 / SFCC-10793 note
  // that lived here): React Router 7.16 strips the single-fetch `.data`
  // suffix BEFORE loaders run (executed proof in the readiness review), so
  // the `/_root.data → "/"` fold this comment used to justify could never
  // match and has been deleted as dead code. The COM-67 dead-logo mechanism
  // it claimed to fix (the shared deadline burning out on `/`, then a 204 +
  // reload) was therefore never fixed by it — that failure now at least
  // LOGS via the homepage fail-soft warn at the catch-all fallback below.
  // `documentPathForDataRedirect` in `cloudflare/worker.server.ts` remains
  // the seatbelt that maps a data-request redirect back to a document URL on
  // the way out.
  const path = url.pathname;

  // SFRA's pipeline alias `/404 → Home-ErrorNotFound` is its branded 404
  // page. If the browser arrives here directly (e.g. via SFRA's own
  // redirect chain when an unknown URL was proxied to SFRA), short-circuit
  // to a Worker 404 instead of round-tripping back to SFRA — otherwise we
  // loop catch-all → SFRA → /404 → catch-all again.
  if (path === "/404") {
    throw new Response("Not Found", { status: 404 });
  }

  // RFC 8615 `/.well-known/appspecific/*` is reserved for browser/agent
  // probes — Chrome DevTools auto-fetches
  // `/.well-known/appspecific/com.chrome.devtools.json` whenever DevTools is
  // open. These are never storefront URLs, so short-circuit to a 404 instead
  // of falling through to the SFRA proxy (which, on a local hostname with no
  // matching SFRA_ORIGIN_* var, throws a hard 500 on every DevTools open).
  if (path.startsWith("/.well-known/appspecific/")) {
    throw new Response("Not Found", { status: 404 });
  }

  // SFCC-served endpoints under `/dw/` (DIS images `/dw/image/*`, OCAPI Data
  // `/dw/data/*`, `/dw/meta/*`) are never React Router pages. They should be
  // proxied to SFRA at Layer 1 (`legacy-routes.ts` `^/dw/image/`,
  // `^/s/…/dw/shop/`) and never arrive here. This is the defense-in-depth
  // backstop (mirrors the `/.well-known/appspecific/` dual-guard above): if the
  // Layer-1 proxy entry is ever missing/misconfigured, short-circuit to a 404
  // so an asset request can NEVER run the Builder page + SCAPI URL-mapping
  // lookup. That page pipeline on image traffic is what exhausted the shared
  // Builder/SCAPI rate-limit budget and produced the 429/offline cascade
  // (SFCC-11887). A 404 on a stray asset is strictly better than a Builder
  // render that 429s real pages.
  if (path.startsWith("/dw/")) {
    throw new Response("Not Found", { status: 404 });
  }

  // Legacy SFRA `/categoryref/<refinements>` URLs encode refinements as trailing
  // path segments (e.g. `/…/all-medicine-and-treatments/categoryref/kt-tape_smartypants_therapik`).
  // Normalize them to the query-param form (`?brand=…`) with a 301 BEFORE any
  // resolution: the clean category URL then flows through the normal pipeline
  // (Builder → redirect bundle → SCAPI category), `parseHecFilters` applies the
  // refinements, and the facet adapter renders active chips. Without this the
  // leaf-id probe treats the refinement blob as a category id → 404 (logged as
  // "[object Object]") + a silently unfiltered PLP. Also consolidates every
  // refinement permutation of a category onto one canonical URL for SEO,
  // mirroring SFRA's `/categoryref.*` canonical strip.
  const categoryRefTarget = categoryRefRedirectTarget(path, url.search);
  if (categoryRefTarget) {
    throw redirect(categoryRefTarget, 301);
  }

  // Builder + redirect-bundle + SCAPI lookups run in parallel — they're
  // independent, and the dispatcher's priority rules apply after all resolve.
  // Overlapping the latency removes the sequential RTT cost (was: builder →
  // then SCAPI; now: max(builder, redirect bundle, SCAPI)).
  //
  // Critical: we wait for BOTH to finish before dispatching to a render
  // loader (`runProductLoader` / `runCategoryLoader`). That's deliberate —
  // a Builder `sfra-proxy` flag must be honored even when SCAPI resolved
  // the URL to PRODUCT/CATEGORY, and we don't want to start streaming PDP
  // or PLP data before we know whether to proxy.
  //
  // Errors propagate on either side (fail-closed): a Builder brown-out
  // shouldn't silently flip a URL into the SCAPI branch, and a SCAPI
  // brown-out shouldn't masquerade as a missing URL.
  const { env: sessionEnv } = getSessionServerContext();
  type HelperKv = Parameters<typeof lookupUrlMapping>[0]["kv"];
  type RedirectKv = Parameters<typeof resolveBundledRedirect>[0]["kv"];

  // Route-truth consult (Phase 2a — SFCC-11861 / resolves SFCC-11785). OPT-IN:
  // runs only when ROUTE_TRUTH_ENABLED === "true", so it's a no-op until enabled
  // per env. When active, a path SFRA owns per the hourly snapshot short-circuits
  // to the SFRA proxy BEFORE the Builder/SCAPI tasks and the category leaf-probe
  // — the fix for isSfraPage pages whose slug also matches a catalog category
  // (e.g. /bilt-labs.html rendering a PLP). Fail-open by construction: disabled
  // var, missing/stale snapshot, unknown path, or any consult error → fall
  // through to today's cascade unchanged.
  if (routeTruthConsultEnabled(sessionEnv)) {
    try {
      const truth = await loadRouteTruth({
        env: sessionEnv,
        siteId: context.preset.siteId,
      });
      if (classifyRoute(truth, path).decision === "sfra") {
        throw new ProxyToSFRASignal("route-truth-sfra", path);
      }
    } catch (routeTruthError) {
      if (routeTruthError instanceof ProxyToSFRASignal) throw routeTruthError;
      console.warn("[catch-all] route-truth consult failed (non-fatal)", {
        path,
        message:
          routeTruthError instanceof Error
            ? routeTruthError.message
            : String(routeTruthError),
      });
    }
  }

  // Bound the Builder page lookup's TOTAL wall clock.
  //
  // Production 2026-08-15: `.data` and document requests on this catch-all
  // parked for 136-173 SECONDS at 10-226ms of CPU, returning 503s and OOMing the
  // isolate. In the same window Builder was timing out repeatedly —
  // `builder-page`, `model page` references, `external-links`,
  // `search-inline-setup` — with every stack running through
  // `runAndCache -> retryOnceOnTransient -> tryBuilderPage -> Promise.all`.
  //
  // Each individual Builder fetch IS capped at 2.5s, but `tryBuilderPage` also
  // awaits `context.traits` + `context.abtastyEvaluation` and its fetch was
  // then retried once (timeouts no longer are, COM-837), so the healthy worst
  // case was ~7s and the degraded case stacks
  // without limit. Nothing bounded the sum — the same gap SFCC-10620 fixed for
  // the PDP loader, one level up. This is the hot path for EVERY catch-all
  // route, so it is where the damage is widest.
  //
  // `null` is not a new degradation: it is the value `tryBuilderPage` already
  // returns for a transient Builder error, and it means "no Builder page" —
  // nothing more. The rest of the cascade still runs: a product / category
  // URL that SCAPI maps renders the Workers PDP / PLP; only a URL nothing maps
  // (or `/`, which only Builder renders) reaches the SFRA fallback. Under a
  // Builder brown-out we serve the next answer in the cascade instead of
  // stalling (COM-852 traced and measured this).
  // The REQUEST's deadline, not a fresh one. This used to be its own 8s
  // budget, which then stacked on top of the PDP/PLP loader's budget further
  // down the same request — 8s + 6s = the 14s parks seen in staging traces.
  // The shared request deadline governs the WHOLE dispatch fan-out below, not
  // just Builder — see the per-task `within` wraps. Renamed from `builderDeadline`
  // when `mappingTask` / `bundledRedirectTask` were brought under the same cap.
  const dispatchDeadline = getRequestDeadline();
  const builderTask = dispatchDeadline.within(
    tryBuilderPage({
      request,
      context,
      path,
      searchParams: url.searchParams,
    }),
    null,
    `builder-page ${path}`,
  );

  // `lookupUrlMapping` skips empty / "/" slugs internally; the index route
  // owns the homepage so this loader shouldn't fire on `/`. Resolve to
  // `null` immediately for `/` to keep the parallel join symmetric.
  //
  // SFCC-11918. `Promise.all` is fail-fast, so a rejected mapping discards a
  // Builder page that was fetched successfully alongside it — and the Builder
  // branch below does not use the mapping at all. During a SLAS mint cooldown
  // that is exactly what happened: `/womens-health.html` (a Builder page)
  // returned "Looks like this page is broken." even though its content had
  // already been retrieved. Degrade to "no SCAPI mapping" instead of rejecting;
  // the cascade then does the right thing on its own (a Builder page still
  // renders in slot 1, and a URL with no Builder page falls through to the
  // catch-all SFRA proxy where an unmapped URL belongs). Only this one error
  // class is softened — any other mapping failure still propagates.
  //
  // Bounded by the shared request deadline for the SAME reason `builderTask`
  // is: `lookupUrlMapping` reaches SCAPI, which needs a SLAS token, and a fresh
  // guest mint on a session-less request (e.g. right after Clear-Site-Data) can
  // HANG rather than throw. Left unwrapped, that stall had no cap at this join —
  // the `.catch` only handles a THROWN throttle — and produced 44-second parks
  // that returned no response headers at ~4ms CPU (2026-09-02, `routes/$` on
  // `/surprisingly-eligible`). `within` caps it at the 8s request budget and
  // degrades to null, emitting `[deadline] url-mapping <path> abandoned` ONLY on
  // overrun (never on a healthy request), which also names the culprit task.
  const mappingTask = dispatchDeadline.within(
    (path && path !== "/"
      ? lookupUrlMapping({
          slug: path,
          api: context.api,
          // Cast through `unknown` — the helper's KVNamespace type comes from
          // `@cloudflare/workers-types` while `sessionEnv.URL_MAPPING_CACHE`
          // resolves through the ambient global declaration. Same runtime
          // type, different declaration paths.
          kv: sessionEnv.URL_MAPPING_CACHE as unknown as HelperKv,
          waitUntil: context.waitUntil,
          // COM-823: a Builder author previewing a just-published URL must see
          // the fresh cascade, not a definitive miss cached up to 60 s ago.
          bypassMissCache: isPreviewing(url.searchParams),
        })
      : Promise.resolve(null)
    ).catch((err: unknown) => {
      if (err instanceof SlasMintThrottledError) {
        console.warn(
          "[catch-all] SLAS mint throttled — continuing without a SCAPI mapping",
          { path },
        );
        return null;
      }
      throw err;
    }),
    null,
    `url-mapping ${path}`,
  );

  // Bounded for symmetry with the two tasks above — a `REDIRECT_CACHE` KV read
  // is normally sub-millisecond, but no await on this universal hot path should
  // be the one that is unbounded. Degrades to "no bundled redirect".
  const bundledRedirectTask = dispatchDeadline.within(
    path && path !== "/"
      ? resolveBundledRedirect({
          kv: sessionEnv.REDIRECT_CACHE as unknown as RedirectKv,
          siteId: context.preset.siteId,
          pathname: url.pathname,
          search: url.search,
        })
      : Promise.resolve(null),
    null,
    `bundled-redirect ${path}`,
  );

  // ── Slow-dispatch watchdog (wedge instrument) ─────────────────────────────
  // This leaf loader is the hot path for every non-home route, so it must NOT
  // log on success — a "join resolved" marker would multiply log volume by
  // total traffic. Instead, arm ONE timer that healthy requests cancel before
  // it fires (the join resolves in ~250ms-1.4s); the only code path that logs
  // is a dispatch still unresolved after SLOW_DISPATCH_MS, and it names which of
  // the three parallel tasks is still pending. That attribution is exactly what
  // was missing from the two proven parks (2026-09-02), which returned no
  // response headers at ~4ms CPU with the culprit task unknowable from the logs.
  // This is the early, richer signal; the per-task `[deadline] … abandoned`
  // lines are the 8s backstop.
  const SLOW_DISPATCH_MS = 3_000;
  const dispatchStartedAt = Date.now();
  const pendingDispatch = new Set<string>(["builder", "redirect", "mapping"]);
  // `.then(clear, clear)`, not `.finally` — a settle-tracker must never turn a
  // task's rejection into an EXTRA unhandled rejection; the `Promise.all` below
  // owns the real error handling.
  const clearPending = (name: string) => () => {
    pendingDispatch.delete(name);
  };
  builderTask.then(clearPending("builder"), clearPending("builder"));
  bundledRedirectTask.then(clearPending("redirect"), clearPending("redirect"));
  mappingTask.then(clearPending("mapping"), clearPending("mapping"));
  const slowDispatchTimer = setTimeout(() => {
    // Everything actionable goes in the MESSAGE STRING, not a structured second
    // arg: Cloudflare Observability indexes only `$metadata.message`, so a
    // `{ pending }` object is invisible to `needle`/`contains` queries (learned
    // 2026-09-03 chasing a REDIRECT_CACHE brownout — the culprit task had to be
    // recovered from the `[deadline] … abandoned` lines instead). Keep it
    // greppable: `pending=[redirect]` names the still-stalled task(s).
    console.warn(
      `[catch-all] slow-dispatch pending=[${[...pendingDispatch].join(",")}] ` +
        `elapsedMs=${Date.now() - dispatchStartedAt} path=${path}`,
    );
  }, SLOW_DISPATCH_MS);

  const [builder, bundledRedirect, mapping] = await Promise.all([
    builderTask,
    bundledRedirectTask,
    mappingTask,
  ]).finally(() => {
    // Always clear: a live timer keeps the isolate alive, the very failure mode
    // the request deadline exists to prevent.
    clearTimeout(slowDispatchTimer);
  });

  // ── Dispatch priority (SFCC-12092) ────────────────────────────────────────
  // The ORDER lives in `evaluateDispatch`, which `/dev/url-trace` also calls.
  // It used to be duplicated here as an if-ladder and re-implemented there,
  // and the two drifted (the route-truth consult existed only here, so the
  // tracer misattributed every route-truth proxy). One function decides; this
  // caller materialises the decision, the tracer renders it.
  //
  // `tryBuilderPage` already returns null for isSfraPage entries and for
  // empty/chrome-only blocks, so `builder != null` IS `builderWouldRender`.
  // The richer isSfraPage/hasBlocks split only exists so the tracer can raise
  // the SFCC-11785 anomaly; it cannot change which slot wins.
  const resourceType = mapping?.resourceType?.toLowerCase() ?? null;
  const resourceId = mapping?.resourceId ?? null;
  const scapiStatus = Number(mapping?.statusCode);
  const decision = evaluateDispatch({
    // Phase 0 already ran above and short-circuited on `sfra` — it must stay
    // ahead of the fan-out or every SFRA-owned path would pay for a Builder +
    // SCAPI round-trip it currently skips. Reaching here means it did not fire.
    routeTruth: null,
    builder: {
      entryFound: Boolean(builder),
      isSfraPage: false,
      hasBlocks: Boolean(builder),
    },
    redirect: bundledRedirect,
    scapiRedirect:
      mapping?.statusCode && mapping.destinationUrl
        ? {
            // COM-102: carry inbound tracking params through the SCAPI
            // redirect. Merged HERE, at construction, because evaluateDispatch
            // maps BOTH the bundled and SCAPI redirect slots onto
            // `outcome.kind === "redirect"` — so the `decision.kind ===
            // "redirect"` branch below throws THIS location and any merge
            // placed after it is unreachable. `resolveBundledRedirect` already
            // merges internally, so both redirect kinds now arrive here final.
            //
            // Still post-cache: lookupUrlMapping caches the raw mapping in KV
            // keyed by slug only, so a merged location must never be written
            // back into it. Mirrors url-trace.server.ts, which builds its
            // scapiRedirect the same way.
            location: mergeTrackingParams(mapping.destinationUrl, url.search),
            // SCAPI returns statusCode as a string despite the SDK type, and
            // RR7's redirect() only treats a numeric init as a status.
            status: Number.isFinite(scapiStatus) ? scapiStatus : 302,
          }
        : null,
    scapiResourceType: resourceType,
    scapiResourceId: resourceId,
  }).outcome;

  // 1. Builder render wins over SCAPI's resourceType — authored pages can
  // override a PRODUCT or CATEGORY URL with a marketing page.
  if (decision.kind === "builder" && builder) {
    // COM-188: the client loads the full block registry lazily
    // (registry-loader.ts), but SSR renders the page body synchronously — so
    // guarantee the registry module is initialized before a builder payload
    // can reach the renderer. Resolves on the microtask queue after the first
    // call per isolate; there is no I/O behind it.
    await loadCustomComponents();
    return data(
      {
        type: "builder" as const,
        pageType: builder.pageType,
        // Surface the chrome flags at the TOP level so layout.tsx's
        // useLayoutChromeFlags (which reads `matchData.hideLayoutHeader` /
        // `matchData.hideLayoutFooter`) can suppress the shared layout chrome for
        // Builder pages that opt out (`useDefaultHeader` / `useDefaultFooter:
        // false`). Buried under `data` they're invisible to the flag walk — for
        // the footer that rendered a SECOND footer (SFCC-11538); for the header
        // the toggle was a silent no-op (SFCC-11840). Matches the article / LMS
        // routes' top-level flag convention.
        hideLayoutHeader: builder.payload.hideLayoutHeader,
        hideLayoutFooter: builder.payload.hideLayoutFooter,
        // Top level for isAddedToBasketModalSuppressed, like the flags above.
        suppressAddedToBasketModal: builder.spender != null,
        spender: builder.spender ?? null,
        // COM-658: self canonical, query stripped, as legacy emits.
        canonical: builder.spender ? `${url.origin}${url.pathname}` : null,
        // COM-841: top level, where DeferSeo reads a route's `seo` and merges
        // it over the root default — so the page keeps exactly ONE robots tag.
        // Absent unless the entry's noIndex switch is on.
        ...(builder.seo ? { seo: builder.seo } : {}),
        data: builder.payload,
      },
      { headers: builder.headers },
    );
  }

  // 2/3. BM URLRedirectMgr bundle, then the SCAPI URL-Rules redirect — both
  // fire before the resourceType dispatch so seasonal/marketing slugs that
  // SCAPI resolves as empty catalog categories still honor SFRA's RedirectURL
  // precedence, and so an ID-based redirect that ALSO carries a resourceType
  // still redirects. `evaluateDispatch` keeps the bundle ahead of the URL Rule.
  if (decision.kind === "redirect") {
    throw redirect(decision.location, decision.status);
  }

  // 3. SCAPI URL-rule redirects are NOT handled by a separate branch here.
  // `evaluateDispatch` folds its `scapi-redirect` slot into
  // `outcome.kind === "redirect"`, so the branch above already fired for them
  // — an `if (mapping?.statusCode && mapping.destinationUrl)` block at this
  // position is unreachable. One previously lived here and carried COM-102's
  // tracking-param merge; because it could never run, SCAPI redirects silently
  // dropped `utm_*` / `iitt` and marketing attribution was lost on every
  // URL-rule 301. The merge now happens where `scapiRedirect` is built, so
  // `decision.location` is final for both redirect kinds. Do not reintroduce a
  // redirect branch below the `decision.kind` switch.

  // 4. resourceType dispatch on the TYPE, not the source. SCAPI casing
  // varies by code path — URL Rules return lowercase (`"category"`,
  // `"product"`, `"content"`), redirects + SDK constants return uppercase —
  // so it is normalized with `.toLowerCase()` above. Categories/products
  // render locally regardless of whether BM resolved them via a URL Rule or a
  // direct ID match; content proxies to SFRA. URLs SFRA owns despite a
  // `category`/`product` URL Rule (e.g. `/hsa-eligibility-list`) are
  // force-proxied at the worker layer via `LEGACY_LIST`, before this loader.
  if (decision.kind === "plp") {
    const resourceId = decision.categoryId;
    const fullPayload = await runCategoryLoader({
      request,
      context,
      categoryId: resourceId,
    });
    const { seo, pageType, ...payload } = fullPayload;
    return data(
      { type: "plp" as const, seo, pageType, data: payload },
      { headers: { "x-page-type": "plp" } },
    );
  }

  if (decision.kind === "pdp") {
    const fullPayload = await runProductLoader({
      request,
      context,
      productId: decision.productId,
    });
    const { seo, pageType, ...payload } = fullPayload;
    // Document Cache-Control is auth-aware (SFCC-11297), mirroring the Builder
    // branch. A `s-maxage` made the rendered PDP document eligible for the
    // shared edge cache (`CacheMiddleware` → `resolveEdgeCacheControl`), whose
    // key (`toCacheKey`) is host + discriminator + url-hash ONLY — cookies and
    // `Vary` are stripped — so personalized content SSR'd into the document
    // leaked cross-user: header auth state, the AFID TPA welcome banner, and
    // (the standout) AB price-test pricing baked into the SSR'd price via the
    // `hec_priceTestVariant` → pricebook swap. We reuse `builderCacheControl`
    // (no preview concept on PDP, so `previewing: false`): a registered shopper
    // gets `private, no-store`; a guest gets `private, max-age=60`. Neither
    // carries `s-maxage`, so `resolveEdgeCacheControl` bails and the document is
    // never edge-stored, and `private` keeps a guest's price-test pricing in
    // their own browser only. The durable fix that re-enables edge caching is
    // price-keying / hole-punching chrome+price (SFCC-11201).
    const isRegistered = await isAuthenticatedUser();
    // `personalized` mirrors the Builder branch: an assigned campaign makes
    // this document a personalization variant — `private` always (gap-window
    // fix, jam 46b8e6e1). Settled by now on any request that got this far
    // (the evaluation starts at context assembly), so the await is a no-op.
    // `?.` mirrors the buildAbTastyBuilderAttributes tolerance contract
    // (minimal test contexts degrade to variant-free, never throw).
    const pdpEvaluation = await context.abtastyEvaluation;
    return data(
      { type: "pdp" as const, seo, pageType, data: payload },
      {
        headers: {
          // Third arg (COM-185 strictlyCookieless) stays false: the PDP
          // document never got the cookieless relaxation (price-test /
          // attribution risk); opts ride fourth.
          "Cache-Control": builderCacheControl(false, isRegistered, false, {
            personalized: (pdpEvaluation?.assigned.length ?? 0) > 0,
          }),
        },
      },
    );
  }

  if (
    decision.kind === "sfra-proxy" &&
    decision.source === "scapi-content-asset"
  ) {
    // SCAPI says SFRA owns the render. Throw the proxy signal so the
    // worker's SSR catch (`cloudflare/worker.server.ts`) forwards the
    // original request to `proxyToSFRA` — same mechanism worker uses for
    // legacy regex matches in `legacy-routes.ts`. The signal is an
    // `Error` subclass (not a thrown `Response`) so it bypasses RR7's
    // `ErrorBoundary` rendering and reaches the worker's catch block.
    console.info("[catch-all] CONTENT_ASSET → throwing ProxyToSFRASignal", {
      path,
      resourceId,
    });
    throw new ProxyToSFRASignal("scapi-content-asset", path);
  }

  // 5. No mapping, no Builder page. Rather than 404'ing here, fall back
  // to SFRA — it's the runtime that knows about everything Workers
  // hasn't taken over yet (legacy `.aspx` URLs, site-specific aliases
  // like `/fsa-deadline-alerts` / `/hsa-401k-maximizer`, partner pages,
  // and anything outside our explicit URL inventory). SFRA returns a
  // proper 404 if it doesn't know the URL either, in which case the
  // proxy passes the 404 back unchanged.
  // No log for the GENERAL case: this is the steady-state happy path (fires
  // on every proxied request) — the Cloudflare invocation log already records
  // the URL, and the ProxyToSFRASignal source ("catch-all-fallback") is on
  // the signal itself.
  // Slot 7 always fires, so `sfra-proxy` is the only outcome that can reach
  // here. Asserted rather than assumed: if a slot is ever added to the shared
  // decision and not handled above, this logs instead of silently proxying a
  // page that should have rendered.
  if (decision.kind !== "sfra-proxy") {
    console.error("[catch-all] unhandled dispatch outcome — proxying to SFRA", {
      path,
      outcome: decision.kind,
    });
  }
  // PR #1042 blocker 10: the HOMEPAGE reaching this fallback is never the
  // happy path — `/` is Workers-owned, so landing here means Builder answered
  // 200-with-no-entry or the shared deadline burned out (the COM-346 class:
  // homepage silently fail-softing to SFRA at up to 68/h, with ZERO signal at
  // any layer). One warn per occurrence makes the shift measurable directly
  // instead of only via the GA4 host-variant mix; keep the message prefix
  // stable — observability queries match on it.
  if (path === "/") {
    console.warn("[catch-all] HOMEPAGE fail-soft → proxying to SFRA", {
      path,
    });
  }
  throw new ProxyToSFRASignal("catch-all-fallback", path);
}

// `pdpDataFromMatch` (the accessor that owns this loader's `type: "pdp"` wrap
// shape) lives in ./pdp-data-from-match.ts — NOT here — because root.tsx
// imports it, and a static `root -> routes/$` import would weld this route's
// entire component graph into the shared chunk on every page (COM-174).

export const meta: Route.MetaFunction = ({ loaderData }) => {
  // PLP + PDP both ride DeferSeo via the hoisted `seo` field — returning
  // an empty meta array lets DeferSeo own the head.
  if (loaderData?.type === "plp" || loaderData?.type === "pdp") {
    return [];
  }
  if (loaderData?.type === "builder") {
    const { canonical } = loaderData;
    return [
      ...builderEntryMeta(loaderData.data.page),
      ...(canonical
        ? [{ tagName: "link" as const, rel: "canonical", href: canonical }]
        : []),
    ];
  }
  return [];
};

export default function CatchAllPage() {
  const loaderData = useLoaderData<typeof loader>();
  useSpenderPageMount(
    loaderData.type === "builder" && loaderData.spender != null,
  );
  switch (loaderData.type) {
    case "builder": {
      const { spender } = loaderData;
      const page = <BuilderPageView payload={loaderData.data} />;
      // COM-474: the page's spender blocks read this provider.
      return spender ? (
        <SpenderProvider data={spender} persist={spender.persist}>
          {page}
        </SpenderProvider>
      ) : (
        page
      );
    }
    case "plp":
      return <CategoryPageContent loaderData={loaderData.data} />;
    case "pdp":
      return <ProductPageContent loaderData={loaderData.data} />;
  }
}
